import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, ImagePlus, TriangleAlert, Upload } from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'

import type {
  ImportProductImagesResponse,
  ProductImageImportOutcome,
} from '@aeris/contracts'
import {
  Badge,
  Button,
  EmptyState,
  Page,
  PageHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { ApiError, importProductImages } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * As fotos do catálogo, num arquivo só.
 *
 * O nome do arquivo dentro do ZIP é o código do produto: `1233.jpg` é a foto do
 * 1233. A loja já guarda as fotos assim, e foi assim que o dono descreveu o que
 * pretende mandar — qualquer outra convenção seria pedir que alguém renomeasse
 * cinco mil arquivos antes de começar.
 *
 * ## Por que o relatório é linha a linha
 *
 * "Importei 4820 de 5307" é um número com que não se faz nada. O que resolve o
 * problema é a lista dos códigos que o sistema não conhece — dá para conferir
 * contra a planilha e descobrir que aquelas fotos são de produtos que nunca
 * foram importados.
 *
 * ## O que ela não faz
 *
 * Não substitui foto existente. Um produto que já tem foto é pulado e aparece
 * dito no relatório: a foto que está lá pode ter sido escolhida por alguém, e
 * um importador que passa por cima transforma esse trabalho em nada sem avisar.
 */

const OUTCOME_LABELS: Record<ProductImageImportOutcome, string> = {
  imported: 'Importada',
  unknown_code: 'Código desconhecido',
  already_has_photo: 'Já tinha foto',
  unreadable: 'Não é uma imagem legível',
  too_large: 'Grande demais',
}

const OUTCOME_TONES: Record<
  ProductImageImportOutcome,
  'success' | 'warning' | 'danger'
> = {
  imported: 'success',
  unknown_code: 'warning',
  already_has_photo: 'warning',
  unreadable: 'danger',
  too_large: 'danger',
}

const countBy = (
  result: ImportProductImagesResponse,
  outcome: ProductImageImportOutcome
): number => result.entries.filter((entry) => entry.outcome === outcome).length

export const ImportarFotosRoute = (): ReactNode => {
  const { allows } = useAuth()
  const fileInput = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ImportProductImagesResponse | null>(null)

  const canImport = allows('catalog.write')

  const upload = useMutation({
    mutationFn: async (file: File) => await importProductImages(file),
    onSuccess: (response) => {
      setError(null)
      setResult(response)
    },
    onError: (cause: unknown) => {
      setResult(null)
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível ler o arquivo.'
      )
    },
  })

  /*
   * Só o que não deu certo fica na tabela.
   *
   * Uma lista de cinco mil linhas onde 4820 dizem "Importada" esconde as 487
   * que pedem uma decisão. O que deu certo já está contado acima.
   */
  const problems = (result?.entries ?? []).filter(
    (entry) => entry.outcome !== 'imported'
  )

  return (
    <Page>
      <PageHeader
        title="Importar fotos"
        description="Um arquivo .zip com as fotos dos produtos. O nome de cada arquivo é o código do produto — 1233.jpg vira a foto do 1233."
      />

      <input
        ref={fileInput}
        type="file"
        accept=".zip,application/zip"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0]
          // O valor é limpo para que mandar o MESMO arquivo de novo, depois de
          // corrigir os nomes, ainda dispare o `change`.
          event.target.value = ''
          if (file !== undefined) upload.mutate(file)
        }}
      />

      {error === null ? null : (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-sm) text-(--danger)"
        >
          <TriangleAlert aria-hidden="true" className="h-4 w-4 shrink-0" />
          {error}
        </p>
      )}

      {upload.isPending ? (
        <div className="flex flex-col items-center gap-3 py-10">
          <Spinner />
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Lendo e recortando as fotos. Um arquivo grande leva alguns minutos —
            não feche esta aba.
          </p>
        </div>
      ) : result === null ? (
        <EmptyState
          icon={<ImagePlus aria-hidden="true" className="h-8 w-8" />}
          title="Nenhum arquivo enviado"
          description="Junte as fotos num .zip, cada uma nomeada com o código do produto: 1233.jpg, 1234.png. Até 400 fotos e 200 MB por vez — se o catálogo for maior, mande em partes."
          action={
            canImport ? (
              <Button
                leadingIcon={<Upload aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  fileInput.current?.click()
                }}
              >
                Escolher o arquivo .zip
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) px-4 py-3">
            <CheckCircle2
              aria-hidden="true"
              className="h-5 w-5 text-(--success)"
            />
            <p className="text-(length:--text-sm) text-(--text-primary)">
              <strong>{result.imported}</strong> de{' '}
              <strong>{result.total}</strong> fotos entraram no catálogo.
            </p>
            {countBy(result, 'unknown_code') === 0 ? null : (
              <Badge tone="warning">
                {countBy(result, 'unknown_code')} sem produto
              </Badge>
            )}
            {countBy(result, 'already_has_photo') === 0 ? null : (
              <Badge tone="warning">
                {countBy(result, 'already_has_photo')} já tinham foto
              </Badge>
            )}

            <Button
              variant="secondary"
              className="ml-auto"
              leadingIcon={<Upload aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                fileInput.current?.click()
              }}
            >
              Enviar outro
            </Button>
          </div>

          {problems.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Todas as fotos do arquivo encontraram o produto delas.
            </p>
          ) : (
            <>
              <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                O que ficou de fora
              </h2>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Arquivo</TableHeaderCell>
                      <TableHeaderCell>Código</TableHeaderCell>
                      <TableHeaderCell>Motivo</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {problems.map((entry) => (
                      <TableRow key={entry.file}>
                        <TableCell>{entry.file}</TableCell>
                        <TableCell className="font-(--font-mono) tabular-nums">
                          {entry.code}
                        </TableCell>
                        <TableCell>
                          <Badge tone={OUTCOME_TONES[entry.outcome]}>
                            {OUTCOME_LABELS[entry.outcome]}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </div>
      )}
    </Page>
  )
}
